"use client"

import { useState, useRef, useEffect } from "react"
import { Link } from "@/i18n/routing"
import Image from "next/image"
import { Menu, ExternalLink, X, Phone } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { useRouter, usePathname } from "@/i18n/routing"
import { useLocale, useTranslations } from "next-intl"
import { localeMetadata, routing, type AppLocale } from "@/i18n/routing"
import { getAllServices } from "@/data/services"
import UTMCampaignBuilderUtils from "@/utils/seo/UTMCampaignBuilderUtils"
import { siteConfig } from "@/config/site"
import { stripLeadingLocales } from "@/i18n/locale-path"
import { LanguageModalSwicher } from "@/components/layout/LanguageModalSwicher"

const SERVICES_MENU_TRIGGER_ID = "desktop-services-menu-trigger"
const SERVICES_MENU_CONTENT_ID = "desktop-services-menu-content"
const ABOUT_MENU_TRIGGER_ID = "desktop-about-menu-trigger"
const ABOUT_MENU_CONTENT_ID = "desktop-about-menu-content"
const MOBILE_MENU_TRIGGER_ID = "mobile-menu-trigger"
const MOBILE_MENU_CONTENT_ID = "mobile-menu-content"
const MOBILE_MENU_TITLE_ID = "mobile-menu-title"

export function Header() {
	const locale = useLocale();
	const pathname = usePathname();
	const router = useRouter();

	const [isOpen, setIsOpen] = useState(false);
	const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false);
	const [showTopShadow, setShowTopShadow] = useState(false);
	const [showBottomShadow, setShowBottomShadow] = useState(false);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	// Dynamic check for scroll shadows based on scroll position
	const checkScrollShadows = () => {
		const container = scrollContainerRef.current;
		if (container) {
			const { scrollTop, scrollHeight, clientHeight } = container;
			const threshold = 20; // Threshold for showing shadows
			
			// Check if content is scrollable at all
			const isScrollable = scrollHeight > clientHeight + 5;
			
			if (!isScrollable) {
				setShowTopShadow(false);
				setShowBottomShadow(false);
				return;
			}
			
			// Show top shadow if scrolled down (content above)
			setShowTopShadow(scrollTop > threshold);
			
			// Show bottom shadow if not at bottom (content below)
			const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;
			setShowBottomShadow(!isAtBottom);
		}
	};

	// Check scroll shadows when menu opens and on scroll
	useEffect(() => {
		if (isOpen) {
			// Initial check when menu opens
			setTimeout(checkScrollShadows, 300);
		} else {
			// Reset shadows when menu closes
			setShowTopShadow(false);
			setShowBottomShadow(false);
		}
	}, [isOpen]);


	const t = useTranslations("navigation");
	const ts = useTranslations("services");
	const services = getAllServices();
	const config = siteConfig;
	const phone = config.contactInfo.phones[0];

	const currentLocale =
		localeMetadata[locale as AppLocale] ??
		localeMetadata[routing.defaultLocale as AppLocale];

	const handleLanguageChange = (newLocale: string) => {
		// Prevent changing to the same locale
		if (newLocale === locale) {
			return;
		}
		
		// Use the real browser URL to avoid stale values; preserve query + hash.
		const href = typeof window !== "undefined" ? window.location.href : "";
		const url = href ? new URL(href) : null;

		const basePath = url ? url.pathname : pathname;
		const cleanPath = stripLeadingLocales(basePath, routing.locales);
		const suffix = url ? `${url.search}${url.hash}` : "";

		// Build explicit locale-prefixed path to ensure UI state and URL always align.
		const nextPath = `/${newLocale}${cleanPath}`.replace(/\/+/g, "/");
		router.replace(`${nextPath}${suffix}`);
	}

	return (
		<header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-backdrop-filter:bg-white/60">
			<div className="container flex h-20 items-center">
				<div className="mx-4 hidden md:flex">
					<Link href="/" className="me-6 flex items-center gap-2">
						<Image
							src="/assets/images/logo/logo-color.svg"
							alt="Uzunov Dental"
							width={180}
							height={100}
							className="h-10 w-auto"
						/>
					</Link>
				</div>

				<NavigationMenu className="hidden lg:flex" dir={currentLocale.direction}>
					<NavigationMenuList>
						{/** Treatments */}
						<NavigationMenuItem key="services" value="services">
							<NavigationMenuTrigger
								id={SERVICES_MENU_TRIGGER_ID}
								aria-controls={SERVICES_MENU_CONTENT_ID}
								className="text-teal-700 hover:text-teal-800"
							>
								{t("services")}
							</NavigationMenuTrigger>
							<NavigationMenuContent id={SERVICES_MENU_CONTENT_ID} aria-labelledby={SERVICES_MENU_TRIGGER_ID}>
								<div className="w-[600px] p-4">
									<div className="grid grid-cols-2 gap-3">
										{services.map((service) => (
											<NavigationMenuLink key={service.id} asChild>
												<Link
													href={service.href}
													className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-teal-50 hover:text-teal-900 focus:bg-teal-50 focus:text-teal-900"
												>
													<div className="text-sm font-medium leading-none">
														{ts(service.id + ".title")}
													</div>
													<p className="line-clamp-2 text-xs leading-snug text-muted-foreground">
														{ts(service.id + ".description")}
													</p>
												</Link>
											</NavigationMenuLink>
										))}
									</div>
									<div className="mt-3 border-t border-teal-100 pt-3">
										<Link
											href="/services"
											className="group flex select-none items-center justify-between rounded-md px-3 py-3 text-sm font-medium text-teal-700 no-underline outline-none transition-colors hover:bg-teal-50 hover:text-teal-900 focus:bg-teal-50 focus:text-teal-900"
										>
											<span>{t("services_dropdown_cta")}</span>
											<span aria-hidden="true" className="transition-transform group-hover:translate-x-1">
												{"->"}
											</span>
										</Link>
									</div>
								</div>
							</NavigationMenuContent>
						</NavigationMenuItem>

						<NavigationMenuItem key="about" value="about">
							<NavigationMenuTrigger
								id={ABOUT_MENU_TRIGGER_ID}
								aria-controls={ABOUT_MENU_CONTENT_ID}
								className="text-teal-700 hover:text-teal-800"
							>
								{t("about.main")}
							</NavigationMenuTrigger>
							<NavigationMenuContent id={ABOUT_MENU_CONTENT_ID} aria-labelledby={ABOUT_MENU_TRIGGER_ID}>
								<div className="grid w-[400px] gap-3 p-4">
									<NavigationMenuLink asChild>
										<Link
											href="/about"
											className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-teal-50 hover:text-teal-900"
										>
											<div className="text-sm font-medium leading-none">{t("about.about_us.title")}</div>
											<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
												{t("about.about_us.description")}
											</p>
										</Link>
									</NavigationMenuLink>
									<NavigationMenuLink asChild>
										<Link
											href="/team"
											className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-teal-50 hover:text-teal-900"
										>
											<div className="text-sm font-medium leading-none">{t("about.team.title")}</div>
											<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
												{t("about.team.description")}
											</p>
										</Link>
									</NavigationMenuLink>
									{/**
									<NavigationMenuLink asChild>
										<Link
											href="/pricing"
											className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-teal-50 hover:text-teal-900"
										>
											<div className="text-sm font-medium leading-none">{t("about.pricing.title")}</div>
											<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
												{t("about.pricing.description")}
											</p>
										</Link>
									</NavigationMenuLink>
									*/}
									<NavigationMenuLink asChild>
										<Link
											href={UTMCampaignBuilderUtils.page("https://www.updent.me/", "header")}
											target="_blank"
											className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-teal-50 hover:text-teal-900"
										>
											<div className="text-sm font-medium leading-none flex items-center gap-1">
												{t("about.training_center.title")}
												<ExternalLink className="h-3 w-3" />
											</div>
											<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
												{t("about.training_center.description")}
											</p>
										</Link>
									</NavigationMenuLink>
								</div>
							</NavigationMenuContent>
						</NavigationMenuItem>

						<NavigationMenuItem key="contact">
							<NavigationMenuLink asChild>
								<Link 
									href="/contact" 
									className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium text-teal-700 transition-colors hover:bg-teal-50 hover:text-teal-900 focus:bg-teal-50 focus:text-teal-900 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
								>
									{t("contact")}
								</Link>
							</NavigationMenuLink>
						</NavigationMenuItem>
					</NavigationMenuList>
				</NavigationMenu>

				<div className="flex flex-1 items-center justify-between gap-2 md:justify-end">
					<div className="w-full flex-1 md:w-auto md:flex-none flex items-center justify-between md:justify-end">
						<Link href="/" className="flex items-center md:hidden mx-4">
							<Image
								src="/assets/images/logo/logo-color.svg"
								alt="Uzunov Dental"
								width={180}
								height={100}
								className="h-8 w-auto"
							/>
						</Link>
							<LanguageModalSwicher
								isOpen={isLanguageDialogOpen}
								onOpenChange={setIsLanguageDialogOpen}
								currentLocale={{
									locale: currentLocale.locale,
									summary: currentLocale.summary,
								}}
								onLanguageChange={handleLanguageChange}
							/>
						</div>

					<Button className="hidden md:inline-flex bg-teal-600 hover:bg-teal-700" asChild>
						<a href={`tel:${phone}`}>
							<Phone className="me-2 h-4 w-4 flex-shrink-0" />
							{t("book_appointment")}
						</a>
					</Button>

						<Sheet open={isOpen} onOpenChange={setIsOpen}>
						<SheetTrigger asChild>
							<Button
								variant="ghost"
								className="me-2 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 lg:hidden"
								id={MOBILE_MENU_TRIGGER_ID}
								aria-controls={MOBILE_MENU_CONTENT_ID}
							>
								<Menu className="h-6 w-6" />
								<span className="sr-only">Toggle Menu</span>
							</Button>
						</SheetTrigger>

						<SheetContent
							id={MOBILE_MENU_CONTENT_ID}
							aria-labelledby={MOBILE_MENU_TITLE_ID}
							side={currentLocale.isRtl ? "right" : "left"}
							className="pe-0 flex flex-col no-slide-animation"
						>
							<SheetHeader className="px-7 shrink-0">
								<SheetTitle id={MOBILE_MENU_TITLE_ID} className="sr-only">Navigation Menu</SheetTitle>
								<div className="flex items-center justify-between">
									<Link href="/" className="flex items-center" onClick={() => setIsOpen(false)}>
										<Image
											src="/assets/images/logo/logo-color.svg"
											alt="Uzunov Dental"
											width={200}
											height={100}
											className="h-16 w-auto"
										/>
									</Link>
									<SheetClose asChild>
										<Button 
											variant="ghost" 
											size="sm" 
											className="text-teal-700 hover:text-teal-800 p-2 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
										>
											<X className="h-6 w-6" />
											<span className="sr-only">Close</span>
										</Button>
									</SheetClose>
								</div>
							</SheetHeader>

							<div className="flex-1 relative">
								{/* Top shadow overlay */}
								{showTopShadow && (
									<div className="absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/10 to-transparent pointer-events-none z-10" />
								)}
								
								{/* Bottom shadow overlay */}
								{showBottomShadow && (
									<div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/50 to-transparent pointer-events-none z-10" />
								)}
								
								<div 
									ref={scrollContainerRef}
									className="absolute inset-0 px-6 pb-6 overflow-y-auto"
									style={{
										WebkitOverflowScrolling: 'touch',
										touchAction: 'pan-y'
									}}
									onScroll={checkScrollShadows}
								>
								<div className="space-y-6">
								<div className="flex flex-col space-y-3">
									<div className="flex flex-col space-y-3">
										<h4 className="font-medium text-teal-700">{t("about.main")}</h4>
										<Link
											href="/about"
											className="text-muted-foreground hover:text-teal-700 ms-4"
											onClick={() => setIsOpen(false)}
										>
											{t("about.about_us.title")}
										</Link>
										<Link
											href="/team"
											className="text-muted-foreground hover:text-teal-700 ms-4"
											onClick={() => setIsOpen(false)}
										>
											{t("about.team.title")}
										</Link>
									</div>

									<Link
										href="/contact"
										className="text-muted-foreground hover:text-teal-700"
										onClick={() => setIsOpen(false)}
									>
										{t("contact")}
									</Link>
								</div>
								
								<div className="flex flex-col space-y-3">
									<h4 className="font-medium text-teal-700">
										{ t("services") } 
									</h4>
									<Link
										href="/services"
										className="text-muted-foreground hover:text-teal-700 ms-4"
										onClick={() => setIsOpen(false)}
									>
										{t("services_overview")}
									</Link>
									{services.map((service) => (
										<Link
											key={service.id}
											href={service.href}
											className="text-muted-foreground hover:text-teal-700 ms-4"
											onClick={() => setIsOpen(false)}
										>
											{ts(service.id + '.title')}
										</Link>
									))}
								</div>
								</div>
								</div>
							</div>
						</SheetContent>
					</Sheet>
				</div>
			</div>
		</header>
	)
}
